import { Injectable } from '@angular/core'

/** What an rg-grid exposes to keyboard shortcuts. */
export interface GridShortcutHost {
    element: HTMLElement
    /** Applies a saved view of this grid; false when the view id isn't one of its views. */
    applyViewById (id: number): boolean
}

/**
 * Tracks mounted grids so grid shortcuts act on one grid only when a screen
 * shows several: the one the user last clicked or focused, else the newest.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutGridHosts {
    private hosts: Array<GridShortcutHost> = []

    private lastActive: GridShortcutHost | null = null

    register (host: GridShortcutHost): () => void {
        this.hosts.push(host)
        const mark = () => { this.lastActive = host }
        host.element.addEventListener('pointerdown', mark, { capture: true })
        host.element.addEventListener('focusin', mark)

        return () => {
            host.element.removeEventListener('pointerdown', mark, { capture: true })
            host.element.removeEventListener('focusin', mark)
            this.hosts = this.hosts.filter(h => h !== host)
            if (this.lastActive === host) this.lastActive = null
        }
    }

    active (): GridShortcutHost | null {
        return this.lastActive ?? this.hosts[this.hosts.length - 1] ?? null
    }

    /** Applies the view on whichever mounted grid owns it, preferring the active one. */
    applyView (id: number): boolean {
        const active = this.active()
        if (active?.applyViewById(id)) return true
        return this.hosts.some(h => h !== active && h.applyViewById(id))
    }
}
